import { Carousel, Modal } from "antd";
import { CarouselRef } from "antd/lib/carousel";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import GalleryCarouselButton from "components/shared/Gallery/GalleryModal/GalleryCarouselButton";
import CustomButton from "rivals/components/shared/CustomButton";
import GalleryModalFooter from "rivals/components/shared/Gallery/GalleryModal/GalleryModalFooter";
import GalleryModalHeader from "rivals/components/shared/Gallery/GalleryModal/GalleryModalHeader";
import Paywall from "rivals/components/shared/Paywall";
import { PAYWALL_VARIANT } from "rivals/components/shared/Paywall/types";
import ShareMenu from "rivals/components/shared/ShareMenu";
import { ShareMenuVariant } from "rivals/components/shared/ShareMenu/types";
import Spinner from "rivals/components/shared/Spinner";
import { Z_INDICES } from "rivals/shared/constants";
import { GalleryImage } from "rivals/shared/interfaces/Content";
import { Prospect } from "rivals/shared/interfaces/Prospect";
import { setData } from "rivals/shared/utils/qa";
import styles from "./GalleryModal.module.scss";
import strings from "./strings";
import { Props } from "./types";

const GalleryModal: FunctionComponent<Props> = ({
  activeAthleteView,
  gallery,
  isSubscribedUser,
  onClose,
  open,
  prospect,
  error
}: Props): JSX.Element | null => {
  const carouselEl = useRef<CarouselRef | null>(null);
  const [slideIndex, setSlideIndex] = useState<number>(0);
  const [showSlideshow, setShowSlideshow] = useState<boolean>(false);
  const showPaywall = !!gallery?.premium && !isSubscribedUser;
  const showSlides = !showPaywall && showSlideshow;
  const showImages = !showPaywall && !showSlideshow;

  const shareMenu = (
    <ShareMenu
      activeAthleteView={activeAthleteView}
      gallery={gallery}
      prospect={prospect}
      variant={prospect ? ShareMenuVariant.ATHLETE : ShareMenuVariant.GALLERY}
    />
  );

  const selectImage = (index: number): void => {
    setShowSlideshow(true);
    setSlideIndex(index);
  };

  useEffect(() => {
    if (showSlideshow && carouselEl.current) {
      carouselEl.current.goTo(slideIndex);
    }
  }, [showSlideshow, slideIndex]);

  const galleryImages = gallery?.images.map(
    (image: GalleryImage, index: number) => (
      <div
        className={styles.imageContainer}
        key={`gallery-image-${image.imageId}-${index}`}
      >
        <CustomButton
          ariaLabel={image.description || `gallery thumbnail ${index}`}
          icon={
            <img
              alt={image.description || ""}
              src={image.thumbnailUrl}
              {...setData("gallery-modal-image-thumbnail")}
            />
          }
          onClick={(): void => selectImage(index)}
        >
          <div className={styles.imageOverlay} />
        </CustomButton>
      </div>
    )
  );

  const carouselImages = gallery?.images.map(
    (image: GalleryImage, index: number) => {
      const renderedProspects = image.prospects.length ? (
        <div>
          {image.prospects
            .map((prospect: Prospect) => prospect.fullName)
            .join(", ")}
        </div>
      ) : null;

      return (
        <div
          className={styles.contentWrapper}
          {...setData("gallery-modal-carousel")}
          key={`carousel-image-${image.imageId}-${index}`}
        >
          <div className={styles.blurredImage}>
            <img
              alt={image.description || ""}
              className={styles.mobile}
              src={image.fullImageUrl}
            />
            <img
              alt={image.description || ""}
              className={styles.desktop}
              src={image.galleryPreviewUrl}
            />
          </div>
          <div className={styles.captionBar}>
            <div className={styles.imageCount}>{` ${index + 1} ${strings.of} ${
              gallery?.images.length
            } ${strings.photos}`}</div>
            {renderedProspects}
            {image.caption && <div>{image.caption}</div>}
            {image.credit && <div>{`${strings.credit}: ${image.credit}`}</div>}
          </div>
        </div>
      );
    }
  );

  if (error) {
    return null;
  }

  return (
    <Modal
      closable={false}
      footer={null}
      onCancel={onClose}
      open={open}
      styles={{
        body: {
          backgroundColor: "rgba(0,0,0,0.8)",
          bottom: 0,
          left: 0,
          padding: 0,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          position: "fixed" as any,
          right: 0,
          top: 0,
          width: "100vw"
        }
      }}
      transitionName=""
      zIndex={Z_INDICES["lightbox-z-index"]}
    >
      {gallery ? (
        <div className={styles.modalContainer}>
          <GalleryModalHeader
            createdAt={new Date(gallery.createdAt).toLocaleDateString()}
            id={gallery.id}
            name={gallery.name}
            onExit={onClose}
            onViewChange={(show: boolean): void => setShowSlideshow(show)}
            shareMenu={shareMenu}
            showPaywall={showPaywall}
            showSlideshow={showSlideshow}
            summary={gallery.summary}
          />
          <div className={styles.content}>
            {showSlides && (
              <Carousel
                arrows={true}
                dots={false}
                effect="fade"
                infinite
                lazyLoad="ondemand"
                nextArrow={<GalleryCarouselButton />}
                prevArrow={<GalleryCarouselButton isPrev />}
                ref={carouselEl}
                rootClassName={styles.carousel}
                slidesToScroll={1}
                slidesToShow={1}
                swipeToSlide={true}
                variableWidth={false}
              >
                {carouselImages}
              </Carousel>
            )}
            {showPaywall && (
              <div className={styles.paywallContainer}>
                <Paywall
                  isNational
                  title={strings.subscribe}
                  variant={PAYWALL_VARIANT.GALLERY}
                />
              </div>
            )}
            {showImages && (
              <div className={styles.imageGalleryWrapper}>
                <div
                  className={styles.imageCount}
                  {...setData("gallery-image-count")}
                >
                  {`${gallery?.images.length || 0} ${strings.photos}`}
                </div>
                <div
                  className={styles.imagesContainer}
                  {...setData("gallery-image-container")}
                >
                  <div className={styles.images}>{galleryImages}</div>
                </div>
              </div>
            )}
          </div>
          <GalleryModalFooter
            onViewChange={(show: boolean): void => setShowSlideshow(show)}
            shareMenu={shareMenu}
            showPaywall={showPaywall}
            showSlideshow={showSlideshow}
          />
        </div>
      ) : (
        <div className={styles.spinnerContainer}>
          <Spinner />
        </div>
      )}
    </Modal>
  );
};

export default GalleryModal;
